/**
 * RetailerJoin — /retailers/join
 *
 * The retailer-staff sign-up page that Pardot invitation emails link to:
 *   /retailers/join?email={{Recipient.Email}}&inv=<campaign code>
 *
 * Reachable by URL only (no nav entry). Same mechanics as the AuthModal's
 * signup view — email + password, then the emailed 4-character code creates
 * the account and signs the person in — with retailer messaging and NO social
 * buttons: the invited address is the one Salesforce knows, so that is the one
 * we want on the account. Social providers can be connected afterwards from
 * /account/security.
 *
 * On load the page parks the invitation hint in the server session
 * (auth-retailer-join-hint.php). Whichever login path then completes — this
 * form, "Sign in instead" (the normal modal), or a social button in that modal —
 * the retailer classifier receives it and queues a membership for approval.
 *
 * Nothing on the link is trusted: ownership of the email is proven by the
 * code, and the retailer link stays "pending" until a Trusscore admin approves.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Store, MailCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAppSettings } from '../context/AppSettingsContext';
import { nativeSignup, nativeResendCode, checkEmailExists, retailerJoinHint } from '../services/api';
import { PasswordField } from '../components/auth/PasswordField';
import { CodeInput } from '../components/auth/CodeInput';
import { isStaffEmail, STAFF_IDP_ALIAS, STAFF_SIGN_IN_NOTICE_KEY } from '../config/staffAuth';
import { authErrorMessage, networkErrorMessage } from '../config/authErrors';

const API_URL = process.env.REACT_APP_API_URL || '';

type View = 'signup' | 'code';

export const RetailerJoin: React.FC = () => {
  const { t } = useTranslation('auth');
  const { settings, isLoading: settingsLoading } = useAppSettings();
  const { user, isChecking, verifySignupCode, openAuthModal } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const inviteCode = (params.get('inv') || '').trim() || null;
  const available = settings.accountsEnabled && settings.nativeAuthEnabled;

  const [view, setView] = useState<View>('signup');
  const [email, setEmail] = useState(() => (params.get('email') || '').trim());
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwValid, setPwValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailTaken, setEmailTaken] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [staffRedirectingUI, setStaffRedirectingUI] = useState(false);
  const staffRedirecting = useRef(false);

  // Code view state (mirrors AuthModal).
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [codeClearSignal, setCodeClearSignal] = useState(0);
  const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    if (codeExpiresAt === null) return;
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [codeExpiresAt]);

  // Park the invitation hint server-side once. If the visitor is already signed
  // in, the server classifies them straight away with it.
  const hintSent = useRef(false);
  useEffect(() => {
    if (!available || hintSent.current) return;
    hintSent.current = true;
    void retailerJoinHint(inviteCode);
  }, [available, inviteCode]);

  const showCodeScreen = (expiresIn?: number) => {
    setCodeError(null);
    setCodeInvalid(false);
    setCodeClearSignal((n) => n + 1);
    setCodeExpiresAt(Date.now() + (expiresIn ?? 900) * 1000);
    setNowTick(Date.now());
  };

  const goToStaffLogin = () => {
    if (staffRedirecting.current) return;
    staffRedirecting.current = true;
    setStaffRedirectingUI(true);
    window.location.assign(
      `${API_URL}/api/auth-login.php?kc_idp_hint=${encodeURIComponent(STAFF_IDP_ALIAS)}&return_to=${encodeURIComponent('/account')}`,
    );
  };

  const checkEmail = async () => {
    const value = email.trim();
    if (!value || !value.includes('@') || isStaffEmail(value)) { setEmailTaken(false); return; }
    setCheckingEmail(true);
    const res = await checkEmailExists(value);
    setCheckingEmail(false);
    setEmailTaken(res.checked && res.exists);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (isStaffEmail(email)) { goToStaffLogin(); return; }
    if (!pwValid) { setError(t('errors.choosePassword')); return; }
    setError(null);
    setSubmitting(true);
    let res;
    try {
      res = await nativeSignup({
        email: email.trim(), password, firstName: firstName.trim(), lastName: lastName.trim(),
      });
    } catch {
      setSubmitting(false);
      setError(networkErrorMessage());
      return;
    }
    setSubmitting(false);
    if (res.status === 'code_sent') {
      showCodeScreen(res.expiresIn);
      setView('code');
    } else if (res.error === 'email_exists') {
      setEmailTaken(true);
    } else {
      setError(authErrorMessage(res, 'auth:errors.createAccountFailed'));
    }
  };

  const handleVerifyCode = async (code: string) => {
    if (verifyingCode) return;
    setCodeError(null);
    setCodeInvalid(false);
    setVerifyingCode(true);
    const res = await verifySignupCode(code);
    setVerifyingCode(false);
    if (res.ok) {
      // Signed in; the account page shows the (pending) retailer membership.
      navigate('/account?welcome=1');
      return;
    }
    setCodeInvalid(true);
    setCodeClearSignal((n) => n + 1);
    if (res.error === 'expired' || res.error === 'too_many') setCodeExpiresAt(0);
    setCodeError(authErrorMessage(res, 'auth:errors.codeIncorrect'));
  };

  const handleResendCode = async () => {
    if (resendingCode) return;
    setResendingCode(true);
    setCodeError(null);
    let res;
    try {
      res = await nativeResendCode();
    } catch {
      setResendingCode(false);
      setCodeError(networkErrorMessage());
      return;
    }
    setResendingCode(false);
    if (res.ok) showCodeScreen(res.expiresIn);
    else setCodeError(authErrorMessage(res, 'auth:errors.resendFailed'));
  };

  const staffEmail = isStaffEmail(email);
  const primaryBtnStyle: React.CSSProperties = {
    background: 'var(--partner-button-bg, #F5A623)',
    color: 'var(--partner-button-fg, #14233a)',
    border: 'none',
  };

  const secondsLeft = codeExpiresAt === null ? null : Math.max(0, Math.round((codeExpiresAt - nowTick) / 1000));
  const codeExpired = secondsLeft !== null && secondsLeft <= 0;
  const mmss = secondsLeft === null ? '' :
    `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  const shell = (body: React.ReactNode) => (
    <div className="container px-0 px-sm-3 py-4" style={{ maxWidth: 520 }}>
      <div className="card shadow-sm overflow-hidden">
        <div
          className="d-flex align-items-center gap-2 fw-semibold px-4 py-3"
          style={{ background: 'var(--partner-modal-header-bg, #14233a)', color: 'var(--partner-modal-header-fg, #ffffff)' }}
        >
          <Store size={18} /> {t('retailerJoin.heading')}
        </div>
        <div className="card-body p-4">{body}</div>
      </div>
    </div>
  );

  // Settings arrive from the server after first paint (a fresh incognito window
  // has no cached copy), so hold the page until they do — otherwise the
  // "not available" card flashes before the form appears.
  if (settingsLoading) {
    return shell(
      <div className="text-center py-4 text-muted">
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
      </div>,
    );
  }

  if (!available) {
    return shell(
      <>
        <h5 className="fw-bold mb-2">{t('retailerJoin.unavailableTitle')}</h5>
        <p className="text-muted small mb-0">{t('retailerJoin.unavailableBody')}</p>
      </>,
    );
  }

  if (!isChecking && user) {
    return shell(
      <>
        <h5 className="fw-bold mb-2">{t('retailerJoin.signedInTitle')}</h5>
        <p className="small mb-3">
          <Trans i18nKey="retailerJoin.signedInBody" ns="auth"
            values={{ who: user.name || user.email || '' }} components={{ b: <strong /> }} />
        </p>
        <button type="button" className="btn fw-semibold" style={primaryBtnStyle} onClick={() => navigate('/account')}>
          {t('retailerJoin.goToAccount')}
        </button>
      </>,
    );
  }

  if (view === 'code') {
    return shell(
      <div className="text-center">
        <MailCheck size={38} className="mb-3" style={{ color: 'var(--partner-primary, #14233a)' }} />
        <p className="mb-1">{t('code.emailedTo', { count: 4 })}</p>
        <p className="fw-semibold mb-3">{email.trim()}</p>

        <CodeInput
          length={4}
          clearSignal={codeClearSignal}
          invalid={codeInvalid}
          disabled={verifyingCode || codeExpired}
          onComplete={handleVerifyCode}
        />

        <div className="mt-3" style={{ minHeight: 24 }}>
          {verifyingCode ? (
            <span className="text-muted small">{t('code.verifying')}</span>
          ) : codeError ? (
            <span className="small text-danger">{codeError}</span>
          ) : codeExpired ? (
            <span className="small text-danger">{t('code.expired')}</span>
          ) : secondsLeft !== null ? (
            <span className="small text-muted">{t('code.expiresIn', { time: mmss })}</span>
          ) : null}
        </div>

        <div className="mt-2">
          <button type="button" className="btn btn-link btn-sm" onClick={handleResendCode} disabled={resendingCode}>
            {resendingCode ? t('code.sending') : t('code.resend')}
          </button>
        </div>

        <p className="text-center small text-muted mt-2 mb-0">
          {t('code.wrongEmail')}{' '}
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
            onClick={() => { setError(null); setView('signup'); }}>{t('code.startOver')}</button>
        </p>
      </div>,
    );
  }

  return shell(
    <form onSubmit={handleSignup} noValidate>
      <p className="small mb-1">{inviteCode ? t('retailerJoin.invitedIntro') : t('retailerJoin.intro')}</p>
      <p className="text-muted small mb-4">{t('retailerJoin.approvalNote')}</p>

      <div className="row g-2 mb-3">
        <div className="col">
          <label htmlFor="rj-first" className="form-label small fw-semibold mb-1">{t('fields.firstName')}</label>
          <input id="rj-first" type="text" className="form-control" autoComplete="given-name"
            autoFocus required value={firstName} disabled={submitting}
            onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div className="col">
          <label htmlFor="rj-last" className="form-label small fw-semibold mb-1">{t('fields.lastName')}</label>
          <input id="rj-last" type="text" className="form-control" autoComplete="family-name"
            required value={lastName} disabled={submitting}
            onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="rj-email" className="form-label small fw-semibold mb-1">{t('fields.email')}</label>
        <input id="rj-email" type="email"
          className={`form-control ${emailTaken ? 'is-invalid' : ''}`} autoComplete="email"
          required value={email} disabled={submitting}
          onChange={(e) => { setEmail(e.target.value); setEmailTaken(false); }}
          onBlur={checkEmail} />
        {checkingEmail ? (
          <div className="form-text">{t('signup.checkingEmail')}</div>
        ) : emailTaken ? (
          <div className="invalid-feedback d-block">
            {t('errors.emailExists')}{' '}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
              onClick={() => openAuthModal('/account', 'login', email.trim())}>{t('signup.signInInstead')}</button>
          </div>
        ) : (
          <div className="form-text">{t('retailerJoin.emailHelp')}</div>
        )}
      </div>

      {staffEmail ? (
        <p className="text-muted small mb-3" role="status">{t(STAFF_SIGN_IN_NOTICE_KEY)}</p>
      ) : (
        <PasswordField
          idPrefix="rj"
          value={password}
          onChange={setPassword}
          confirmValue={confirmPassword}
          onConfirmChange={setConfirmPassword}
          disabled={submitting}
          onValidChange={setPwValid}
        />
      )}

      {error && <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">{error}</div>}

      <button type="submit" className="btn w-100 fw-semibold"
        disabled={submitting || !email.trim() || staffRedirectingUI
          || (!staffEmail && (!pwValid || !firstName.trim() || !lastName.trim() || emailTaken))}
        style={primaryBtnStyle}>
        {staffEmail ? (staffRedirectingUI ? t('redirecting') : t('continue'))
          : submitting ? t('signup.busy') : t('retailerJoin.submit')}
      </button>

      <p className="text-center small text-muted mt-3 mb-0">
        {t('retailerJoin.haveAccount')}{' '}
        <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
          onClick={() => openAuthModal('/account', 'login', email.trim() || undefined)}>{t('retailerJoin.signIn')}</button>
      </p>
    </form>,
  );
};

export default RetailerJoin;
